/**
 * Table utility functions for extracting and converting table data.
 *
 * Reason (CONTRATO v3, O4): this module's functions operate on chat-rendered
 * DOM tables (it reads `container.querySelector('table')`), NOT on native
 * editor ProseMirror nodes. So the DOM path stays. To keep ONE import surface
 * for table serialization, we re-export the canonical node serializer here —
 * anything that has a native table NODE (not DOM) should use `serializeTable`.
 */
export { serializeTable, default as serializeTableDefault } from '../../lib/tableSerializer';

/**
 * Extract table data into a matrix format
 */
export const extractTableMatrix = (table) => {
  if (!table) return [];
  const rows = Array.from(table.querySelectorAll('tr'));
  return rows.map((row) => {
    const cells = Array.from(row.querySelectorAll('th, td'));
    return cells.map((cell) => (cell.innerText || cell.textContent || '').replace(/\s+/g, ' ').trim());
  });
};

/**
 * Convert table data to Markdown format
 */
export const tableToMarkdown = (rows, hasHeader) => {
  if (!rows.length) return '';

  const headerRow = hasHeader
    ? rows[0]
    : rows[0].map((_, idx) => `Col ${idx + 1}`);
  const bodyRows = hasHeader ? rows.slice(1) : rows;
  const columnCount = headerRow.length || (bodyRows[0]?.length ?? 0);

  const formatRow = (row) => `| ${row.map((cell) => (cell ?? '')).join(' | ')} |`;
  const separator = `| ${Array.from({ length: columnCount }).map(() => '---').join(' | ')} |`;

  const lines = [
    formatRow(headerRow),
    separator,
    ...bodyRows.map(formatRow),
  ];

  return lines.join('\n');
};

/**
 * Escape CSV values properly
 */
export const escapeCsvValue = (value = '') => {
  const needsQuotes = /[",\n]/.test(value);
  const cleaned = value.replace(/"/g, '""');
  return needsQuotes ? `"${cleaned}"` : cleaned;
};

/**
 * Convert table data to CSV format
 */
export const tableToCSV = (rows) => {
  if (!rows.length) return '';
  return rows
    .map((row) => row.map((cell) => escapeCsvValue(cell ?? '')).join(','))
    .join('\n');
};

/**
 * Convert table data to JSON format
 */
export const tableToJSON = (rows, hasHeader) => {
  if (!rows.length) return '[]';

  const headerRow = hasHeader
    ? rows[0]
    : rows[0].map((_, idx) => `col_${idx + 1}`);
  const normalizedHeaders = headerRow.map((header, idx) => (header?.trim() ? header.trim() : `col_${idx + 1}`));
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const objects = dataRows.map((row) =>
    normalizedHeaders.reduce((acc, header, idx) => {
      acc[header] = row[idx] ?? '';
      return acc;
    }, {})
  );

  return JSON.stringify(objects, null, 2);
};

/**
 * Build all copy formats for a table
 */
export const buildTableCopyFormats = (table) => {
  const rows = extractTableMatrix(table);
  const hasHeader = !!table.querySelector('th');
  const plainText = rows.map((row) => row.join('\t')).join('\n');

  return {
    plainText,
    html: table.outerHTML,
    markdown: tableToMarkdown(rows, hasHeader),
    csv: tableToCSV(rows),
    json: tableToJSON(rows, hasHeader),
  };
};

/**
 * Get table copy content in specific format
 */
export const getTableCopyContent = (container, format = 'default') => {
  if (!container) return null;
  const table = container.querySelector('table');
  if (!table) return null;

  const formats = buildTableCopyFormats(table);

  switch (format) {
    case 'markdown':
      return { text: formats.markdown };
    case 'html':
      return { text: formats.html, html: formats.html };
    case 'json':
      return { text: formats.json };
    case 'csv':
      return { text: formats.csv };
    default:
      return { text: formats.plainText, html: formats.html };
  }
};
